import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Circle,
  Copy,
  LockKeyhole,
  Mail,
  MessageSquare,
  MousePointerClick,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import type { ReferralPayoutMethod } from '@zeroth/referral-contract';
import { useClerk, useUser } from '@clerk/clerk-react';
import { errorMessage } from '../lib/api';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { Button } from '../components/Button';
import { cn, money } from '../lib/utils';
import {
  SUPPORT_EMAIL,
  customerOffer,
  percent,
  tierFor,
  usePortalDashboard,
  usePortalPath,
  type PortalDashboard,
} from './portalContext';
import { usePayoutDetails } from './usePartnerDashboard';
import { hasCopiedLink, useCopy } from './useCopy';


const STATUS_LABELS: Record<string, { label: string; tone: string }> = {
  active: { label: 'Paying', tone: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  trialing: { label: 'In trial', tone: 'bg-primary/10 text-primary' },
  past_due: { label: 'Past due', tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-400' },
  canceled: { label: 'Canceled', tone: 'bg-muted text-muted-foreground' },
  signed_up: { label: 'Signed up', tone: 'bg-muted text-muted-foreground' },
};
export const statusLabel = (status: string) =>
  STATUS_LABELS[status] ?? { label: status.replace(/_/g, ' '), tone: 'bg-muted text-muted-foreground' };

export const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export function PageHeader({ eyebrow, title, children }: { eyebrow: string; title: string; children?: ReactNode }) {
  return (
    <header className="mb-6">
      <p className="text-xs font-semibold uppercase tracking-widest text-primary">{eyebrow}</p>
      <h1 className="mt-1.5 text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h1>
      {children && <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{children}</p>}
    </header>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <section className={cn('min-w-0 rounded-xl border border-border bg-card p-5', className)}>{children}</section>;
}

export function CardTitle({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <h2 className="font-semibold">{title}</h2>
      {action}
    </div>
  );
}

export function Stat({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <Card>
      <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="mt-2 break-words text-2xl font-semibold tracking-tight tabular-nums">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{note}</p>
    </Card>
  );
}

function CopyField({ value, code, label, copyLabel = 'Copy' }: { value: string; code?: string; label: string; copyLabel?: string }) {
  const copy = useCopy(code);
  return (
    <div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          readOnly
          aria-label={label}
          value={value}
          onFocus={(event) => event.currentTarget.select()}
          onClick={(event) => event.currentTarget.select()}
          className="min-w-0 flex-1 rounded-lg border border-input bg-background px-3 py-2.5 font-mono text-sm"
        />
        <Button onClick={() => copy.copy(value)}>
          {copy.copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copy.copied ? 'Copied' : copyLabel}
        </Button>
      </div>
      {copy.failed && (
        <p role="alert" className="mt-2 text-xs text-destructive">
          Copy failed. Select the text and copy it manually.
        </p>
      )}
    </div>
  );
}

function TierCard({ dashboard }: { dashboard: PortalDashboard }) {
  const { partner, program } = dashboard;
  const tier = tierFor(program, partner.rateBasisPoints);
  const progress = partner.progress;
  const next = progress && tierFor(program, progress.nextRate);
  return (
    <Card>
      <CardTitle title="Your tier" />
      <div className="flex items-baseline gap-3">
        <p className="text-3xl font-semibold tracking-tight">{tier.name}</p>
        <p className="text-lg font-semibold text-primary">{percent(partner.rateBasisPoints)} recurring</p>
      </div>
      {progress ? (
        <div className="mt-5">
          <div className="flex flex-wrap justify-between gap-2 text-sm">
            <span className="flex items-center gap-1.5 font-medium">
              {progress.requiresReview && <LockKeyhole className="h-4 w-4" aria-hidden="true" />}
              Next: {progress.nextName} · {percent(progress.nextRate)}
            </span>
            <span className="text-muted-foreground">
              {money(partner.monthlyRevenueCents)} / {money(progress.targetCents)} a month
            </span>
          </div>
          <div
            role="progressbar"
            aria-label={'Progress to ' + progress.nextName}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress.percent)}
            className="mt-2.5 h-2 overflow-hidden rounded-full bg-primary/10"
          >
            <div className="h-full rounded-full bg-primary" style={{ width: progress.percent + '%' }} />
          </div>
          <p className="mt-2.5 text-xs text-muted-foreground">
            {progress.remainingCents === 0
              ? 'Revenue milestone reached. Zeroth will review your account for the next tier.'
              : `${money(progress.remainingCents)} more in monthly subscription revenue from your referrals to reach ${progress.nextName}.`}
            {progress.requiresReview && progress.remainingCents > 0 && next?.qualification &&
              ` ${next.name} also requires ${next.qualification.toLowerCase()}.`}
          </p>
        </div>
      ) : (
        <p className="mt-4 text-sm text-muted-foreground">You’re at the highest partner tier.</p>
      )}
    </Card>
  );
}

function EarningsChart({ monthly }: { monthly: PortalDashboard['monthly'] }) {
  const max = Math.max(...monthly.map((m) => m.cents), 0);
  return (
    <Card>
      <CardTitle title="Commission by month" />
      <div className="flex h-44 items-end gap-2 sm:gap-3" role="list" aria-label="Commission by month">
        {monthly.map((m) => {
          const label = new Date(m.month + '-15T00:00:00Z').toLocaleString('en-US', {
            month: 'short',
            timeZone: 'UTC',
          });
          return (
            <div key={m.month} role="listitem" className="flex h-full min-w-0 flex-1 flex-col justify-end gap-2">
              <span className="truncate text-center text-[11px] tabular-nums text-muted-foreground">
                {m.cents ? money(m.cents) : ''}
              </span>
              <div
                className={cn('w-full rounded-md', m.cents ? 'bg-primary' : 'bg-muted')}
                style={{ height: max ? Math.max(4, (m.cents / max) * 100) + '%' : '4px' }}
                aria-label={`${label}: ${money(m.cents)}`}
              />
              <span className="text-center text-xs text-muted-foreground">{label}</span>
            </div>
          );
        })}
      </div>
      {max === 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          Commissions show up here when the sellers you refer make their first payment.
        </p>
      )}
    </Card>
  );
}

function GettingStarted({ dashboard }: { dashboard: PortalDashboard }) {
  const portalPath = usePortalPath();
  const { partner, totals } = dashboard;
  const dismissKey = 'zeroth-partner-checklist-dismissed:' + partner.code;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(dismissKey) === '1';
    } catch {
      return false;
    }
  });
  const steps = [
    { done: true, label: 'Create your partner account', to: null },
    { done: hasCopiedLink(partner.code), label: 'Copy your referral link and share it', to: 'links' },
    { done: !!partner.payoutMethod, label: 'Add where you want to be paid', to: 'earnings' },
    { done: totals.referrals > 0, label: 'Get your first referral', to: 'links' },
  ];
  if (dismissed || steps.every((step) => step.done)) return null;
  return (
    <Card className="mb-6 border-primary/30 bg-primary/5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">You’re in. Your referral link is live.</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Anyone who signs up through your link gets {customerOffer(dashboard.program)}, and you earn on every
            payment they make.
          </p>
        </div>
        <button
          type="button"
          aria-label="Dismiss getting started"
          onClick={() => {
            setDismissed(true);
            try {
              window.localStorage.setItem(dismissKey, '1');
            } catch {
              // Dismissal lasts for this visit only.
            }
          }}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-4">
        <CopyField value={partner.referralUrl} code={partner.code} label="Your referral link" />
      </div>
      <ol className="mt-4 grid gap-2 sm:grid-cols-2">
        {steps.map((step) => (
          <li key={step.label} className="flex items-center gap-2 text-sm">
            {step.done ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" aria-label="Done" />
            ) : (
              <Circle className="h-4 w-4 shrink-0 text-muted-foreground" aria-label="To do" />
            )}
            {!step.done && step.to ? (
              <Link to={portalPath(step.to)} className="font-medium text-primary hover:underline">
                {step.label}
              </Link>
            ) : (
              <span className={cn(step.done && 'text-muted-foreground line-through')}>{step.label}</span>
            )}
          </li>
        ))}
      </ol>
    </Card>
  );
}

function ReferralRows({ referrals }: { referrals: PortalDashboard['referrals'] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="py-2.5 pr-3 font-medium">Seller</th>
            <th className="py-2.5 pr-3 font-medium">Joined</th>
            <th className="py-2.5 pr-3 font-medium">Status</th>
            <th className="py-2.5 text-right font-medium">You’ve earned</th>
          </tr>
        </thead>
        <tbody>
          {referrals.map((ref) => {
            const status = statusLabel(ref.status);
            return (
              <tr key={ref.id} className="border-b border-border last:border-0">
                <td className="py-3 pr-3 font-medium">{ref.label}</td>
                <td className="py-3 pr-3 text-muted-foreground">{shortDate(ref.joinedAt)}</td>
                <td className="py-3 pr-3">
                  <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', status.tone)}>
                    {status.label}
                  </span>
                </td>
                <td className="py-3 text-right font-medium tabular-nums">{money(ref.earnedCents)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function EmptyReferrals() {
  const portalPath = usePortalPath();
  return (
    <div className="py-8 text-center">
      <Users className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
      <p className="mt-3 text-sm font-medium">No referrals yet</p>
      <p className="mt-1 text-sm text-muted-foreground">
        Sellers appear here as soon as they create an account through your link.
      </p>
      <Link
        to={portalPath('links')}
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
      >
        Share your link <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

export function OverviewPage() {
  useDocumentTitle('Partner overview · Zeroth');
  const dashboard = usePortalDashboard();
  const portalPath = usePortalPath();
  const { user } = useUser();
  const { totals, program } = dashboard;
  const funnel = [
    { label: 'Link clicks', value: totals.clicks, icon: MousePointerClick },
    { label: 'Sign-ups', value: totals.referrals, icon: UserPlus },
    { label: 'Paying sellers', value: totals.paying, icon: Users },
  ];
  return (
    <>
      <PageHeader eyebrow={user?.firstName ? `Welcome back, ${user.firstName}` : 'Welcome back'} title="Your performance" />
      <GettingStarted dashboard={dashboard} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="This month" value={money(totals.thisMonthCents)} note="Commission on payments this month" />
        <Stat label="Pending" value={money(totals.pendingCents)} note={`In the ${program.payouts.holdDays}-day hold`} />
        <Stat label="Available" value={money(totals.availableCents)} note="Paid on the next monthly payout" />
        <Stat label="Lifetime earned" value={money(totals.earnedCents)} note={`${money(totals.paidCents)} paid out so far`} />
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <EarningsChart monthly={dashboard.monthly} />
        <TierCard dashboard={dashboard} />
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <Card>
          <CardTitle title="Your funnel" />
          <ul className="space-y-3">
            {funnel.map(({ label, value, icon: Icon }) => (
              <li key={label} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2.5 text-sm text-muted-foreground">
                  <Icon className="h-4 w-4" aria-hidden="true" /> {label}
                </span>
                <span className="text-lg font-semibold tabular-nums">{value.toLocaleString('en-US')}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">
            Clicks count each browser once. The first referral link a seller clicks gets credit for{' '}
            {program.attributionDays} days.
          </p>
        </Card>
        <Card>
          <CardTitle
            title="Recent referrals"
            action={
              dashboard.referrals.length > 0 && (
                <Link to={portalPath('referrals')} className="text-sm font-medium text-primary hover:underline">
                  View all
                </Link>
              )
            }
          />
          {dashboard.referrals.length ? <ReferralRows referrals={dashboard.referrals.slice(0, 5)} /> : <EmptyReferrals />}
        </Card>
      </div>
    </>
  );
}

const shareMessages = (url: string, offer: string) => [
  {
    title: 'Short post',
    text: `Amazon sellers: Zeroth runs repricing, restock, shipments, and profit analytics in one place. Get ${offer} with my link: ${url}`,
  },
  {
    title: 'Direct message',
    text: `Hey! If you’re still juggling separate tools for your Amazon store, take a look at Zeroth. It handles repricing, restock, shipments, and P&L in one platform. You get ${offer} through my link: ${url}`,
  },
  {
    title: 'Email',
    text: `Subject: ${offer.split(' off')[0]} off Zeroth for your Amazon store\n\nHi,\n\nI wanted to share Zeroth, an all-in-one platform for Amazon sellers: AI repricing, restock planning, FBA shipments, and profit analytics. If you sign up through my link, you’ll get ${offer}, after a free trial:\n\n${url}\n\nHappy to answer any questions.`,
  },
];

function MessageCard({ title, text }: { title: string; text: string }) {
  const copy = useCopy();
  return (
    <Card className="flex flex-col">
      <CardTitle
        title={title}
        action={
          <Button size="sm" variant="outline" onClick={() => copy.copy(text)}>
            {copy.copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copy.copied ? 'Copied' : 'Copy'}
          </Button>
        }
      />
      <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{text}</p>
    </Card>
  );
}

export function LinksPage() {
  useDocumentTitle('Links & sharing · Zeroth partners');
  const { partner, program } = usePortalDashboard();
  const url = partner.referralUrl;
  const offer = customerOffer(program, 'your');
  const messages = shareMessages(url, offer);
  const encoded = encodeURIComponent(url);
  const pitch = encodeURIComponent(`Get ${offer} of Zeroth, the all-in-one platform for Amazon sellers:`);
  const shares = [
    { label: 'Post on X', href: `https://x.com/intent/post?text=${pitch}&url=${encoded}` },
    { label: 'Share on LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}` },
    { label: 'Share on Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}` },
  ];
  return (
    <>
      <PageHeader eyebrow="Links & sharing" title="Share your link, start earning">
        Every seller who creates a Zeroth account through this link is credited to you, and they get{' '}
        {customerOffer(program)} automatically.
        They can also enter your code during signup, before connecting their first Amazon store.
      </PageHeader>
      <Card>
        <CardTitle title="Your referral link" />
        <CopyField value={url} code={partner.code} label="Your referral link" copyLabel="Copy link" />
        {partner.referralCode && <div className="mt-4">
          <p className="mb-2 text-sm font-medium">Your referral code</p>
          <CopyField value={partner.referralCode.toUpperCase()} code={partner.code} label="Your referral code" copyLabel="Copy code" />
          <p className="mt-2 text-xs text-muted-foreground">Share this code in your bio or over the phone. New sellers enter it on the welcome screen before connecting Amazon.</p>
        </div>}
        <div className="mt-4 flex flex-wrap gap-2">
          {shares.map((share) => (
            <a
              key={share.label}
              href={share.href}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
            >
              {share.label}
            </a>
          ))}
          <a
            href={`mailto:?subject=${encodeURIComponent(messages[2].text.split('\n')[0].replace('Subject: ', ''))}&body=${encodeURIComponent(messages[2].text.split('\n\n').slice(1).join('\n\n'))}`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
          >
            <Mail className="h-4 w-4" aria-hidden="true" /> Email
          </a>
          <a
            href={`sms:?&body=${encodeURIComponent(messages[1].text)}`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
          >
            <MessageSquare className="h-4 w-4" aria-hidden="true" /> Text message
          </a>
        </div>
      </Card>
      <h2 className="mb-3 mt-8 font-semibold">Ready-to-send messages</h2>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        {messages.map((message) => (
          <MessageCard key={message.title} {...message} />
        ))}
      </div>
      <Card className="mt-8">
        <CardTitle title="How credit works" />
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>
            Your link credits a new seller who creates their account within {program.attributionDays} days of clicking
            it.
          </li>
          <li>If a seller clicks more than one partner’s link, the first link they clicked gets the credit.</li>
          <li>Manual codes can only be applied during signup, before the first successful Amazon connection. They cannot replace an existing referral.</li>
          <li>Existing Zeroth customers, your own accounts, and duplicate accounts don’t qualify.</li>
        </ul>
      </Card>
    </>
  );
}

export function ReferralsPage() {
  useDocumentTitle('Referrals · Zeroth partners');
  const { referrals, totals } = usePortalDashboard();
  return (
    <>
      <PageHeader eyebrow="Referrals" title="Sellers you’ve referred">
        Track every seller who signed up through your link and what you’ve earned from each.
      </PageHeader>
      <div className="mb-3 grid grid-cols-3 gap-3">
        <Stat label="Link clicks" value={totals.clicks.toLocaleString('en-US')} note="Unique browsers" />
        <Stat label="Sign-ups" value={totals.referrals.toLocaleString('en-US')} note="Accounts credited to you" />
        <Stat label="Paying" value={totals.paying.toLocaleString('en-US')} note="Active paid subscriptions" />
      </div>
      <Card>{referrals.length ? <ReferralRows referrals={referrals} /> : <EmptyReferrals />}</Card>
    </>
  );
}

function PayoutDetailsForm({ dashboard }: { dashboard: PortalDashboard }) {
  const save = usePayoutDetails();
  const methods = dashboard.program.payouts.methods;
  const [method, setMethod] = useState<ReferralPayoutMethod | undefined>(
    dashboard.partner.payoutMethod ?? methods[0]?.id
  );
  const selected = methods.find((m) => m.id === method);
  const [handle, setHandle] = useState(dashboard.partner.payoutHandle ?? '');
  const [saved, setSaved] = useState(false);
  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    if (!method) return;
    save.mutate({ method, handle: handle.trim() }, { onSuccess: () => setSaved(true) });
  }
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-[180px_minmax(0,1fr)]">
        <label className="text-sm">
          <span className="mb-1.5 block font-medium">Method</span>
          <select
            value={method ?? ''}
            onChange={(event) => setMethod(event.target.value as ReferralPayoutMethod)}
            className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm"
          >
            {methods.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block font-medium">Send payouts to</span>
          <input
            required
            minLength={3}
            maxLength={120}
            value={handle}
            onChange={(event) => setHandle(event.target.value)}
            placeholder={selected?.handleLabel}
            className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm"
          />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save payout details'}
        </Button>
        {saved && !save.isPending && (
          <span role="status" className="flex items-center gap-1.5 text-sm text-primary">
            <Check className="h-4 w-4" /> Saved
          </span>
        )}
        {save.isError && (
          <span role="alert" className="text-sm text-destructive">
            {errorMessage(save.error)}
          </span>
        )}
      </div>
    </form>
  );
}

export function EarningsPage() {
  useDocumentTitle('Earnings & payouts · Zeroth partners');
  const dashboard = usePortalDashboard();
  const { totals, payouts, partner, program } = dashboard;
  const { holdDays } = program.payouts;
  const minimumPayout = money(program.payouts.minimumCents);
  return (
    <>
      <PageHeader eyebrow="Earnings & payouts" title="Your earnings">
        Commissions are paid monthly by the Zeroth team to the payout method you choose below.
      </PageHeader>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Pending" value={money(totals.pendingCents)} note={`In the ${holdDays}-day hold`} />
        <Stat label="Available" value={money(totals.availableCents)} note={`Paid monthly once ${minimumPayout}+`} />
        <Stat label="Paid out" value={money(totals.paidCents)} note={`${payouts.length} ${payouts.length === 1 ? 'payout' : 'payouts'}`} />
        <Stat label="Lifetime earned" value={money(totals.earnedCents)} note={`At ${percent(partner.rateBasisPoints)} today`} />
      </div>
      {totals.adjustmentCents < 0 && (
        <p role="note" className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          A refund or chargeback after a payout left a {money(-totals.adjustmentCents)} adjustment. It comes out of future
          commissions before your next payout.
        </p>
      )}
      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardTitle title="Where to send your payouts" />
          {!partner.payoutMethod && (
            <p className="mb-4 rounded-lg bg-primary/5 p-3 text-sm text-primary">
              Add a payout method so we can pay you as soon as your balance is available.
            </p>
          )}
          <PayoutDetailsForm dashboard={dashboard} />
        </Card>
        <Card>
          <CardTitle title="How payouts work" />
          <ol className="space-y-3 text-sm text-muted-foreground">
            <li>
              <span className="font-medium text-foreground">1. You earn on every payment.</span> When a seller you referred
              pays, your commission at your current tier rate is recorded.
            </li>
            <li>
              <span className="font-medium text-foreground">2. A {holdDays}-day hold.</span> Commissions stay pending for{' '}
              {holdDays} days in case of refunds or chargebacks.
            </li>
            <li>
              <span className="font-medium text-foreground">3. Monthly payouts.</span> Once your available balance reaches{' '}
              {minimumPayout}, we pay it out with the monthly payout run. Smaller balances roll forward.
            </li>
          </ol>
        </Card>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <EarningsChart monthly={dashboard.monthly} />
        <Card>
          <CardTitle title="Payout history" />
          {payouts.length ? (
            <ul className="divide-y divide-border">
              {payouts.map((payout) => (
                <li key={payout.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{shortDate(payout.paidAt)}</p>
                    <p className="truncate text-xs text-muted-foreground">Ref. {payout.reference}</p>
                  </div>
                  <p className="font-semibold tabular-nums">{money(payout.amountCents)}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Your payouts will appear here after your first payout.
            </p>
          )}
        </Card>
      </div>
    </>
  );
}

export function SettingsPage() {
  useDocumentTitle('Settings · Zeroth partners');
  const dashboard = usePortalDashboard();
  const { user } = useUser();
  const { openUserProfile, signOut } = useClerk();
  const { partner } = dashboard;
  return (
    <>
      <PageHeader eyebrow="Settings" title="Account settings" />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardTitle title="Your account" />
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Name</dt>
              <dd className="font-medium">{user?.fullName || 'Not set'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Email</dt>
              <dd className="truncate font-medium">{user?.primaryEmailAddress?.emailAddress}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Partner since</dt>
              <dd className="font-medium">{shortDate(partner.joinedAt)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Tier</dt>
              <dd className="font-medium">
                {tierFor(dashboard.program, partner.rateBasisPoints).name} · {percent(partner.rateBasisPoints)}
              </dd>
            </div>
          </dl>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => openUserProfile()}>
              Manage name, email & password
            </Button>
            <Button variant="ghost" onClick={() => void signOut({ redirectUrl: window.location.origin })}>
              Sign out
            </Button>
          </div>
        </Card>
        <Card>
          <CardTitle title="Payout details" />
          <PayoutDetailsForm dashboard={dashboard} />
        </Card>
        <Card className="lg:col-span-2">
          <CardTitle title="Commission tiers" />
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {dashboard.program.tiers.map((tier) => (
              <div
                key={tier.rateBasisPoints}
                className={cn(
                  'rounded-lg border p-3',
                  tier.rateBasisPoints === partner.rateBasisPoints ? 'border-primary bg-primary/5' : 'border-border'
                )}
              >
                <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">{tier.name}</p>
                <p className="mt-1 text-2xl font-semibold">{percent(tier.rateBasisPoints)}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {tier.minimumMonthlyCents === 0
                    ? tier.qualification
                    : `${money(tier.minimumMonthlyCents)}/mo · ${tier.qualification.toLowerCase()}`}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Questions about your account or payouts? Email{' '}
            <a className="text-primary hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </Card>
      </div>
    </>
  );
}
